import { useMemo, useRef } from 'react';

import type { MenuData } from 'src/types/types';
import { groupMenuItems } from 'src/utils/groupMenuItems';
import { useMenuNavigation } from 'src/hooks/useMenuNavigation';
import { useScrolled } from 'src/hooks/useScrolled';
import { CONTENT_ANCHOR_ID, PRIORITY_IMAGE_COUNT } from 'src/constants/layout';
import { Header } from 'src/components/Header/Header';
import { CafeInfo } from 'src/components/CafeInfo/CafeInfo';
import { CategoryNav } from 'src/components/CategoryNav/CategoryNav';
import { CategorySection } from 'src/components/CategorySection/CategorySection';
import { Footer } from 'src/components/Footer/Footer';
import { Hero } from 'src/components/Hero/Hero';

type CafeMenuProps = {
  data: MenuData;
};

export const CafeMenu = ({ data }: CafeMenuProps) => {
  const { cafe, categories, menuItems, tags } = data;

  const grouped = useMemo(
    () => groupMenuItems(categories, menuItems),
    [categories, menuItems],
  );

  const scrolled = useScrolled();
  const heroRef = useRef<HTMLDivElement>(null);

  const {
    activeGroup,
    hasSubcategories,
    topActiveId,
    activeSubId,
    onTopInView,
    onSubInView,
    handleSelectTopCategory,
    handleSelectSubcategory,
  } = useMenuNavigation(grouped, heroRef);

  if (!activeGroup) return null;

  return (
    <>
      <Header>
        <CafeInfo cafe={cafe} />
      </Header>
      <main>
        <CategoryNav
          categories={grouped.map((g) => g.category)}
          activeId={topActiveId}
          scrolled={scrolled}
          onSelectCategory={handleSelectTopCategory}
        />
        {hasSubcategories && activeGroup.subgroups.length > 0 && (
          <CategoryNav
            categories={activeGroup.subgroups.map((g) => g.category)}
            activeId={activeSubId}
            scrolled={scrolled}
            onSelectCategory={handleSelectSubcategory}
            secondary
          />
        )}
        {cafe.heroImage && (
          <div ref={heroRef}>
            <Hero image={cafe.heroImage} />
          </div>
        )}
        <div id={CONTENT_ANCHOR_ID}>
          {hasSubcategories ? (
            <>
              {activeGroup.directItems.length > 0 && (
                <CategorySection
                  key={`${activeGroup.category.id}-direct`}
                  category={activeGroup.category}
                  items={activeGroup.directItems}
                  tagConfig={tags}
                  priorityCount={PRIORITY_IMAGE_COUNT}
                />
              )}
              {activeGroup.subgroups.map(({ category, items }, index) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  items={items}
                  tagConfig={tags}
                  onInView={onSubInView}
                  headingLevel={3}
                  priorityCount={
                    activeGroup.directItems.length === 0 && index === 0
                      ? PRIORITY_IMAGE_COUNT
                      : 0
                  }
                />
              ))}
            </>
          ) : (
            <>
              {grouped.map(({ category, directItems }, index) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  items={directItems}
                  tagConfig={tags}
                  onInView={onTopInView}
                  priorityCount={index === 0 ? PRIORITY_IMAGE_COUNT : 0}
                />
              ))}
            </>
          )}
        </div>
      </main>
      <Footer cafe={cafe} />
    </>
  );
};
